/**
 * Aufgaben in ZEO-Antworten (27.10.2026, Aufgabenverwaltung 2.0 im großen ZEO): ersetzt die statischen Einzelkarten durch eine Liste mit echter
 * Auswahl-Checkbox je Aufgabe, Suchfeld, Statusfilter und „Alle sichtbaren auswählen“. Die Auswahl gehört der Seite (stabile IDs, bleibt über
 * Filter und Nachrichten erhalten) – sie ändert NIE eine Aufgabe; Aktionen laufen über die Aufgaben-2.0-Dialoge mit Vorschau + Bestätigung.
 * Gelöschte/ersetzte Aufgaben erscheinen nur im ausdrücklich gewählten Filter „Gelöscht“.
 */
import { useEffect, useMemo, useState } from 'react';
import { ExternalLink, ListChecks, Search } from 'lucide-react';
import { betreuungAPI } from '@/api/betreuungAPI';
import { filterAufgaben, statusGruppe, umschalten, alleGefilterten, alleGefiltertenGewaehlt } from '@/lib/aufgabenAuswahl';

const FILTER = [['alle', 'Alle'], ['offen', 'Offen'], ['vorschlag', 'KI-Vorschläge'], ['erledigt', 'Erledigt'], ['geloescht', 'Gelöscht']];
const GRUPPEN_LABEL = { erledigt: 'erledigt', vorschlag: 'KI-Vorschlag', geloescht: 'gelöscht' };
const datum = (d) => (d ? new Date(String(d).slice(0, 10)).toLocaleDateString('de-DE') : null);

export default function ZeoAufgabenListe({ refs, auswahl, onAuswahl, onOeffnen, msgId, stand = 0 }) {
  const [details, setDetails] = useState({});
  const [filter, setFilter] = useState('alle');
  const [q, setQ] = useState('');

  useEffect(() => {
    let aktiv = true;
    const ids = (refs || []).map((r) => r.id).filter(Boolean).slice(0, 100);
    Promise.all(ids.map((id) => betreuungAPI.getAufgabe(id).then((a) => [id, a]).catch(() => [id, { id, status: 'nicht_vorhanden' }])))
      .then((paare) => { if (aktiv) setDetails(Object.fromEntries(paare)); });
    return () => { aktiv = false; };
  }, [refs, stand]);                                   // stand: nach Erledigen/Zusammenführen aktuellen Status neu laden

  // Kartenangabe + aktueller Stand aus der Aufgabe (Status/Klient/Fälligkeit); ohne Antwort bleibt die Karte wie geliefert
  const liste = useMemo(() => (refs || []).map((r) => {
    const d = details[r.id];
    return d ? { ...r, ...d, titel: d.titel || r.titel, status: d.status || r.status } : r;
  }), [refs, details]);
  const sichtbar = filterAufgaben(liste, filter, q);
  const ausgeblendet = liste.length - filterAufgaben(liste, 'alle').length;
  const alleSichtbarGewaehlt = alleGefiltertenGewaehlt(auswahl, sichtbar);

  if (!liste.length) return null;
  return (
    <div className="mt-2 border border-violet-100 rounded-xl bg-white p-3 max-w-2xl" data-testid="zeo-aufgaben-liste" data-msg={msgId}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="flex items-center gap-1 text-xs font-medium text-slate-600"><ListChecks size={14} className="text-violet-500" />{liste.length} Aufgabe(n)</span>
        <div className="relative flex-1 min-w-[10rem]">
          <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="In diesen Aufgaben suchen …" data-testid="zeo-aufgaben-suche"
            className="w-full text-xs border rounded-md pl-6 pr-2 py-1 focus:outline-none focus:ring-1 focus:ring-violet-300" />
        </div>
        <button type="button" onClick={() => onAuswahl(alleGefilterten(auswahl, sichtbar))} disabled={!sichtbar.length} data-testid="zeo-aufgaben-alle"
          className="text-xs px-2 py-1 rounded-md border border-slate-200 hover:bg-slate-50 disabled:opacity-40">
          {alleSichtbarGewaehlt ? 'Sichtbare abwählen' : 'Alle sichtbaren auswählen'}
        </button>
      </div>
      <div className="flex flex-wrap gap-1 mt-2" role="tablist">
        {FILTER.map(([k, l]) => (
          <button key={k} type="button" onClick={() => setFilter(k)} data-testid={`zeo-aufgaben-filter-${k}`} aria-pressed={filter === k}
            className={`text-xs px-2 py-0.5 rounded-full border ${filter === k ? 'bg-violet-600 text-white border-violet-600' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
            {l} ({filterAufgaben(liste, k).length})
          </button>
        ))}
      </div>
      {filter !== 'geloescht' && ausgeblendet > 0 && <p className="text-[11px] text-slate-400 mt-1">{ausgeblendet} gelöschte/ersetzte Aufgabe(n) ausgeblendet</p>}
      <ul className="mt-2 divide-y divide-slate-100">
        {sichtbar.map((a) => {
          const gewaehlt = auswahl.has(a.id);
          const g = statusGruppe(a.status);
          return (
            <li key={a.id} className={`flex items-start gap-2 py-1.5 ${gewaehlt ? 'bg-blue-50/60' : ''}`} data-testid="zeo-task-card" data-aufgabe-id={a.id}>
              <input type="checkbox" className="mt-1 h-4 w-4 accent-blue-600" checked={gewaehlt} onChange={() => onAuswahl(umschalten(auswahl, a))}
                aria-label={`Aufgabe auswählen: ${a.titel || ''}`} title="Auswählen (ändert die Aufgabe nicht)" data-testid={`zeo-aufgabe-select-${a.id}`} />
              <div className="min-w-0 flex-1">
                <p className={`text-sm ${g === 'erledigt' || g === 'geloescht' ? 'text-slate-400 line-through' : 'text-slate-800'}`} data-testid="zeo-task-card-titel">{a.titel || 'Aufgabe'}
                  {GRUPPEN_LABEL[g] && <span className="ml-2 text-[10px] uppercase tracking-wide text-slate-500 border rounded px-1 no-underline inline-block">{GRUPPEN_LABEL[g]}</span>}
                </p>
                <p className="text-xs text-slate-400" data-testid="zeo-task-card-meta">
                  {[a.status, a.prioritaet ? `Priorität ${a.prioritaet}` : null, (a.faellig_am || a.faellig) ? `fällig ${datum(a.faellig_am) || a.faellig}` : null,
                    a.klient_name || a.betreuter_name].filter(Boolean).join(' · ')}
                </p>
              </div>
              <button type="button" onClick={() => onOeffnen && onOeffnen(a)} data-testid="zeo-task-open"
                className="text-xs px-2 py-1 rounded-md bg-violet-50 text-violet-700 hover:bg-violet-100 flex items-center gap-1 shrink-0">
                <ExternalLink size={12} /> Aufgabe öffnen
              </button>
            </li>
          );
        })}
        {!sichtbar.length && <li className="text-xs text-slate-400 py-2">Keine Aufgabe in diesem Filter{q ? ` zu „${q}“` : ''}.</li>}
      </ul>
    </div>
  );
}
